import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from "react";
import { VmClient } from "@/api/client";
import { VmClientProvider } from "@/api/context";
import { signInRedirect } from "@/hooks/useAuth";
import { useLoginClient, useSupabaseAuth } from "../auth";

const GateSignInButton = () => {
  const { session } = useSupabaseAuth();
  const isAnonymous = !session || session.user?.is_anonymous;
  if (!isAnonymous) return null;
  return (
    <button
      type="button"
      onClick={signInRedirect}
      className="rounded-lg border border-white/[0.08] bg-white/[0.04] px-4 py-2 text-[13px] font-medium text-zinc-400 transition-colors hover:bg-white/[0.08] hover:text-zinc-200"
    >
      Sign In
    </button>
  );
};

const VmError = ({ onRetry }: { onRetry: () => void }) => {
  return (
    <div
      className="flex h-screen flex-col items-center justify-center gap-3 bg-zinc-950"
      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
    >
      <p className="text-sm font-semibold text-zinc-200">
        Something went wrong
      </p>
      <p className="max-w-md text-center text-xs text-zinc-400">
        We couldn't connect to your session. Please refresh the page to try again.
      </p>
      <button
        onClick={onRetry}
        className="mt-2 rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm font-medium text-zinc-100 transition-colors hover:bg-zinc-800"
      >
        Retry
      </button>
      <GateSignInButton />
    </div>
  );
};

export const VmProvider = ({ children }: { children: ReactNode }) => {
  const login = useLoginClient();
  const [vmClient, setVmClient] = useState<VmClient | null>(null);
  const [vmError, setVmError] = useState<string | null>(null);
  const connectingRef = useRef(false);

  const connectVm = useCallback(async () => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    setVmClient(null);
    setVmError(null);
    try {
      const client = await VmClient.create(login);
      setVmClient(client);
    } catch (err) {
      console.error("VM connection failed:", err);
      setVmError(err instanceof Error ? err.message : String(err));
    } finally {
      connectingRef.current = false;
    }
  }, [login]);

  useEffect(() => {
    if (!vmClient && !vmError && !connectingRef.current) {
      connectVm();
    }
  }, [vmClient, vmError, connectVm]);

  const value = useMemo(
    () => ({
      client: vmClient,
      status: vmError ? ("error" as const) : vmClient ? ("ready" as const) : ("connecting" as const),
      error: vmError,
      retry: connectVm,
    }),
    [vmClient, vmError, connectVm],
  );

  return (
    <VmClientProvider value={value}>
      {vmError ? <VmError onRetry={connectVm} /> : children}
    </VmClientProvider>
  );
};
